import { apiInstance, gql } from "@/services/httpAdapter";

// Interfaces/Types
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { IPayPlanMatchesResponse, IPayPlansResponse } from "@/PayPlans/types/api";

// Helpers
import { errorLogger } from "@/Common/helpers/logger";

// Fragments
import {
	CallActivityPerformanceFragment,
	CommissionTiersFragment,
	ELRAndHROBonusFragment,
	InputValueFragment,
	LongevityFragment,
	MetricFragment,
	TopPerformerBonusFragment,
} from "@PayPlans/api/fragments/commons";

import { CustomerSatisfactionFragment } from "@PayPlans/api/fragments/salesAssociates";
import {
	AdvisorDefaultFragment,
	FollowUpPerformanceBonusFragment,
	RetentionFragment,
	HolidayPayFragment,
} from "@PayPlans/api/fragments/service/advisors";

// Retrieve sales pay plans list
export async function getAdvisorPayPlans(locationId: string, position: string) {
	const query = gql`
		query getAdvisorPayPlans($locationId: String!, $position: String!) {
			getAdvisorPayPlans(locationId: $locationId, position: $position) {
				_id
				name
				position
				locked
				available
			}
		}
	`;

	const variables = {
		locationId,
		position,
	};

	try {
		const response: GraphqlQueryResponse<"getAdvisorPayPlans", IPayPlansResponse> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getAdvisorPayPlans ?? [];
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

// Retrieve sales pay plans
export async function getAdvisorPayPlan(locationId: string, id: string, position: string) {
	const query = gql`
		query getAdvisorPayPlan($locationId: String!, $id: String!, $position: String!) {
			getAdvisorPayPlan(locationId: $locationId, id: $id, position: $position) {
				_id
				locationId
				name
				position
				default {
					...AdvisorDefault
				}
				followUpPerformanceBonus {
					...FollowUpPerformanceBonus
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				customerSatisfaction {
					...CustomerSatisfaction
				}
				longevity {
					...Longevity
				}
				retentionPay {
					...Retention
				}
				holidayPay {
					...HolidayPay
				}
				topAdvisorsBonus {
					...TopPerformerBonus
				}
				elr {
					...ELRAndHROBonus
				}
				hro {
					...ELRAndHROBonus
				}
				locked
				available
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${AdvisorDefaultFragment}
		${CallActivityPerformanceFragment}
		${CommissionTiersFragment}
		${CustomerSatisfactionFragment}
		${FollowUpPerformanceBonusFragment}
		${InputValueFragment}
		${LongevityFragment}
		${MetricFragment}
		${TopPerformerBonusFragment}
		${ELRAndHROBonusFragment}
		${RetentionFragment}
		${HolidayPayFragment}
	`;

	const variables = {
		locationId,
		id,
		position,
	};

	try {
		//Replace Object with PayPlan type
		const response: GraphqlQueryResponse<"getAdvisorPayPlan", Object> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getAdvisorPayPlan;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getAdvisorPayPlansSidebarTree(locationId: string, position: string, dontGetAdditionalPositions = false) {
	const query = gql`
		query getAdvisorPayPlansSidebarTree($locationId: String!, $position: String!, $dontGetAdditionalPositions: Boolean) {
			getAdvisorPayPlansSidebarTree(
				locationId: $locationId
				position: $position
				dontGetAdditionalPositions: $dontGetAdditionalPositions
			) {
				defaultPayPlans {
					defaultPayPlan {
						_id
						name
						position
						pdfAvailability
					}
					newVehiclePayPlan {
						_id
						name
						position
						pdfAvailability
					}
					preOwnedVehiclePayPlan {
						_id
						name
						position
						pdfAvailability
					}
				}
				available {
					_id
					name
					position
					pdfAvailability
				}
				inProgress {
					_id
					name
					position
					pdfAvailability
				}
				currentlyAssigned {
					_id
					name
					position
					pdfAvailability
				}
				previouslyAssigned {
					_id
					name
					position
					pdfAvailability
				}
			}
		}
	`;

	const variables = {
		locationId,
		position,
		dontGetAdditionalPositions,
	};

	try {
		//Replace Object with PayPlan type
		const response: GraphqlQueryResponse<"getAdvisorPayPlansSidebarTree", Object> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getAdvisorPayPlansSidebarTree ?? [];
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

// Retrieve sales pay plans full and partial matches
export async function getAdvisorPayPlanMatches(locationId: string, payPlanId: string, position: string) {
	const query = gql`
		query getAdvisorPayPlanMatches($locationId: String!, $id: String!, $position: String!) {
			getAdvisorPayPlanMatches(id: $id, locationId: $locationId, position: $position) {
				locationId
				payPlanId
				fullMatchList
				partialMatchList
			}
		}
	`;

	const variables = {
		id: payPlanId,
		locationId,
		position,
	};

	try {
		const response: GraphqlQueryResponse<"getAdvisorPayPlanMatches", IPayPlanMatchesResponse> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getAdvisorPayPlanMatches;
	} catch (err) {
		errorLogger(err as Error);
		return {
			locationId,
			payPlanId,
			fullMatchList: [],
			partialMatchList: [],
		};
	}
}
